"use client";

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

const ReviewImageGallery = ({ images = [], altPrefix = 'Bewertungsbild', thumbSize = 72 }) => {
    const list = (Array.isArray(images) ? images : [])
        .map((item) => (typeof item === 'string' ? item : item?.url))
        .filter(Boolean);

    const [isOpen, setIsOpen] = useState(false);
    const [startIndex, setStartIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mounted, setMounted] = useState(false);

    const swiperRef = useRef(null);
    const stripRef = useRef(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    const open = (index) => {
        setStartIndex(index);
        setActiveIndex(index);
        setIsOpen(true);
    };

    const close = useCallback(() => setIsOpen(false), []);

    const slidePrev = useCallback(() => swiperRef.current?.slidePrev(), []);
    const slideNext = useCallback(() => swiperRef.current?.slideNext(), []);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                e.stopPropagation();
                slidePrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                slideNext();
            }
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [isOpen, close, slidePrev, slideNext]);

    useEffect(() => {
        if (!isOpen) return;
        const el = stripRef.current?.children?.[activeIndex];
        el?.scrollIntoView?.({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }, [activeIndex, isOpen]);

    if (list.length === 0) return null;

    return (
        <>
            <style jsx global>{`
                .rig-thumbs {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-top: 10px;
                }
                .rig-thumb {
                    padding: 0;
                    border: 1px solid #d6d9de;
                    border-radius: 8px;
                    background: #fff;
                    overflow: hidden;
                    cursor: zoom-in;
                    flex-shrink: 0;
                    transition: border-color 0.2s ease, transform 0.2s ease;
                }
                .rig-thumb:hover,
                .rig-thumb:focus-visible {
                    border-color: var(--color-primary, #1a1a1a);
                    transform: translateY(-1px);
                    outline: none;
                }
                .rig-thumb img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .rig-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 2147483647;
                    background: rgba(0, 0, 0, 0.9);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    padding: 20px;
                }
                .rig-close {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    width: 44px;
                    height: 44px;
                    border: none;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.3);
                    color: #fff;
                    font-size: 24px;
                    line-height: 1;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 5;
                }
                .rig-close:hover {
                    background: rgba(255, 255, 255, 0.45);
                }
                .rig-stage {
                    position: relative;
                    width: 90vw;
                    max-width: 1200px;
                    height: 65vh;
                }
                .rig-stage .swiper {
                    height: 100%;
                }
                .rig-slide {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                }
                .rig-slide img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                .rig-arrow {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 5;
                    width: 44px;
                    height: 44px;
                    border: none;
                    border-radius: 50%;
                    background: #f4f4f4;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background-color 0.2s ease;
                }
                .rig-arrow:hover:not(:disabled) {
                    background: #fff;
                }
                .rig-arrow:disabled {
                    display: none;
                }
                .rig-arrow svg {
                    width: 18px;
                    height: 18px;
                    fill: #1a1a1a;
                }
                .rig-arrow--left {
                    left: 15px;
                }
                .rig-arrow--left svg {
                    transform: rotate(180deg);
                }
                .rig-arrow--right {
                    right: 15px;
                }
                .rig-strip-wrap {
                    width: 90vw;
                    max-width: 800px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    padding: 6px;
                    backdrop-filter: blur(10px);
                }
                .rig-strip {
                    display: flex;
                    gap: 8px;
                    overflow-x: auto;
                    scrollbar-width: none;
                }
                .rig-strip::-webkit-scrollbar {
                    display: none;
                }
                .rig-strip button {
                    flex-shrink: 0;
                    width: 70px;
                    height: 70px;
                    padding: 0;
                    border: 2px solid transparent;
                    border-radius: 6px;
                    overflow: hidden;
                    background: none;
                    cursor: pointer;
                    opacity: 0.7;
                    transition: opacity 0.2s ease, border-color 0.2s ease;
                }
                .rig-strip button:hover {
                    opacity: 1;
                }
                .rig-strip button.is-active {
                    opacity: 1;
                    border-color: #fff;
                }
                .rig-strip img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            `}</style>

            <div className="rig-thumbs">
                {list.map((url, index) => (
                    <button
                        key={`${url}-${index}`}
                        type="button"
                        className="rig-thumb"
                        style={{ width: thumbSize, height: thumbSize }}
                        aria-label={`${altPrefix} ${index + 1} von ${list.length} vergrößern`}
                        onClick={() => open(index)}
                    >
                        <img src={url} alt={`${altPrefix} ${index + 1}`} loading="lazy" />
                    </button>
                ))}
            </div>

            {mounted &&
                isOpen &&
                createPortal(
                    <div
                        className="rig-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Bildergalerie"
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="rig-close"
                            aria-label="Galerie schließen"
                            onClick={(e) => {
                                e.stopPropagation();
                                close();
                            }}
                        >
                            ×
                        </button>

                        <div className="rig-stage" onClick={(e) => e.stopPropagation()}>
                            <Swiper
                                initialSlide={startIndex}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                            >
                                {list.map((url, index) => (
                                    <SwiperSlide key={`${url}-${index}`}>
                                        <div className="rig-slide" onClick={close}>
                                            <img
                                                src={url}
                                                alt={`${altPrefix} ${index + 1}`}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>

                            {list.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        className="rig-arrow rig-arrow--left"
                                        aria-label="Vorheriges Bild"
                                        disabled={activeIndex === 0}
                                        onClick={slidePrev}
                                    >
                                        <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
                                            <path d="m6.71 21.71-1.42-1.42 9.3-9.29-9.3-9.29L6.71.29 17.41 11 6.71 21.71Z" />
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="rig-arrow rig-arrow--right"
                                        aria-label="Nächstes Bild"
                                        disabled={activeIndex === list.length - 1}
                                        onClick={slideNext}
                                    >
                                        <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
                                            <path d="m6.71 21.71-1.42-1.42 9.3-9.29-9.3-9.29L6.71.29 17.41 11 6.71 21.71Z" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>

                        {list.length > 1 && (
                            <div className="rig-strip-wrap" onClick={(e) => e.stopPropagation()}>
                                <div className="rig-strip" ref={stripRef}>
                                    {list.map((url, index) => (
                                        <button
                                            key={`${url}-${index}`}
                                            type="button"
                                            className={index === activeIndex ? 'is-active' : ''}
                                            aria-label={`Bild ${index + 1} anzeigen`}
                                            onClick={() => swiperRef.current?.slideTo(index)}
                                        >
                                            <img src={url} alt={`Vorschau ${index + 1}`} loading="lazy" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default ReviewImageGallery;